import React, { useState, useEffect } from "react";
import {
  Building2,
  Search,
  AlertCircle,
  CheckCircle,
  XCircle,
  MoreHorizontal,
  MapPin,
  Mail,
  Phone,
  Users,
  Eye,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Alert, AlertDescription } from "./ui/alert";
import { Separator } from "./ui/separator";
import { toast } from "sonner";
import { Switch } from "./ui/switch";
import { ApiResponse } from "../types/common";
import { organizationsApi, usersApi, ApiError } from "../utils/api";

interface Organization {
  _id: string;
  createdAt: string;
  city?: string;
  organizationName: string;
  state?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  organizationType: string;
  disabled: boolean;
  userId: string;
  totalLicensesUsed: number;
  totalLicensesPurchased: number;
  accountType: string;
  userPlanId?: string;
  planName?: string;
  org_manual_approval_enabled: boolean;
}

export function Organizations() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const loadOrganizations = async (page = 1) => {
    try {
      setIsLoading(true);
      setError(null);

      const params: Record<string, any> = { page, limit: 20 };
      if (filterStatus === "active") params.disabled = false;
      if (filterStatus === "disabled") params.disabled = true;

      const response: ApiResponse = await organizationsApi.getAll(params);

      if (response.success) {
        setOrganizations(response.data.organizations || []);
        setPagination(response.data.pagination || null);
      } else {
        throw new Error(response.message || "Failed to load organizations");
      }
    } catch (err) {
      const errorMessage =
        err instanceof ApiError
          ? `${err.message} (${err.status})`
          : "Failed to load organizations";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizations(currentPage);
  }, [currentPage, filterStatus]);

  const filteredOrganizations = organizations.filter((org) => {
    const matchesSearch =
      (org.organizationName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (org.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (org.city || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (org.organizationType || "").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType =
      filterType === "all" || org.organizationType === filterType;

    return matchesSearch && matchesType;
  });

  const orgTypes = Array.from(
    new Set(organizations.map((o) => o.organizationType).filter(Boolean))
  );

  const activeCount = organizations.filter((o) => !o.disabled).length;
  const disabledCount = organizations.filter((o) => o.disabled).length;
  const totalLicenses = organizations.reduce(
    (sum, o) => sum + (o.totalLicensesPurchased || 0),
    0
  );
  const usedLicenses = organizations.reduce(
    (sum, o) => sum + (o.totalLicensesUsed || 0),
    0
  );

  const handleViewDetail = (org: Organization) => {
    setSelectedOrg(org);
    setShowDetailDialog(true);
  };

  const handleToggleManualApproval = async (org: Organization, value: boolean) => {
    try {
      const res = await organizationsApi.update(org._id, { org_manual_approval_enabled: value });
      if (res && res.success === false) {
        toast.error(res.message || "Failed to update manual charge setting");
        return;
      }
      toast.success(
        `Manual charge ${value ? "enabled" : "disabled"} for ${org.organizationName}`
      );
      loadOrganizations(currentPage);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to update manual charge setting");
    }
  };

  const handleToggleStatus = async (org: Organization) => {
    try {
      // The list endpoint may omit userId — fetch the full org doc to get it
      let userId = org.userId;
      if (!userId) {
        const detail = await organizationsApi.getById(org._id);
        const raw = detail?.data;
        userId = raw?.userId ?? raw?.organization?.userId;
      }

      if (!userId) {
        toast.error("Cannot update status: organization has no linked user account");
        return;
      }

      // disabled lives on the users collection, not the Organizations collection
      await usersApi.updateUserStatus(userId, !org.disabled);
      toast.success(
        `Organization ${org.disabled ? "activated" : "disabled"} successfully`
      );
      loadOrganizations(currentPage);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to update organization status");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Organizations</h1>
            <p className="text-muted-foreground">
              Manage all organizations on the platform.
            </p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-2">
                  <div className="h-4 bg-muted rounded w-20"></div>
                  <div className="h-8 bg-muted rounded w-16"></div>
                  <div className="h-3 bg-muted rounded w-24"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="animate-pulse flex gap-4">
                  <div className="h-4 bg-muted rounded flex-1"></div>
                  <div className="h-4 bg-muted rounded w-24"></div>
                  <div className="h-4 bg-muted rounded w-16"></div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Organizations</h1>
          <p className="text-muted-foreground">
            Manage all organizations on the platform.
          </p>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Organizations
            </CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {pagination ? pagination.total : organizations.length}
            </div>
            <p className="text-xs text-muted-foreground">All registered orgs</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeCount}</div>
            <p className="text-xs text-muted-foreground">Currently active</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Licenses Purchased
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalLicenses}</div>
            <p className="text-xs text-muted-foreground">
              {usedLicenses} used
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Disabled</CardTitle>
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{disabledCount}</div>
            <p className="text-xs text-muted-foreground">
              Disabled organizations
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search organizations..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {orgTypes.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Organization</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Licenses</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Allow Manual Charge</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredOrganizations.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center py-10 text-muted-foreground"
                >
                  No organizations found.
                </TableCell>
              </TableRow>
            ) : (
              filteredOrganizations.map((org) => (
                <TableRow key={org._id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium">{org.organizationName}</div>
                        {org.firstName && (
                          <div className="text-xs text-muted-foreground">
                            {org.firstName} {org.lastName}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{org.organizationType}</Badge>
                  </TableCell>
                  <TableCell>
                    {org.city || org.state ? (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {[org.city, org.state].filter(Boolean).join(", ")}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-0.5">
                      {org.email && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Mail className="h-3 w-3" />
                          <span className="truncate max-w-[130px]">{org.email}</span>
                        </div>
                      )}
                      {org.phone && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Phone className="h-3 w-3" />
                          {org.phone}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <span className="font-medium">{org.totalLicensesUsed}</span>
                      <span className="text-muted-foreground">
                        /{org.totalLicensesPurchased}
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 w-20 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{
                          width: `${
                            org.totalLicensesPurchased > 0
                              ? Math.min(
                                  (org.totalLicensesUsed /
                                    org.totalLicensesPurchased) *
                                    100,
                                  100
                                )
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {org.planName || "—"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => handleToggleManualApproval(org, !org.org_manual_approval_enabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                        org.org_manual_approval_enabled ? "bg-green-500" : "bg-gray-300"
                      }`}
                      title={org.org_manual_approval_enabled ? "Disable manual charge" : "Enable manual charge"}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          org.org_manual_approval_enabled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </TableCell>
                  <TableCell>
                    {org.disabled ? (
                      <Badge variant="destructive" className="gap-1">
                        <XCircle className="h-3 w-3" />
                        Disabled
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="gap-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                      >
                        <CheckCircle className="h-3 w-3" />
                        Active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(org.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleViewDetail(org)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleToggleStatus(org)}
                        >
                          {org.disabled ? (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Activate
                            </>
                          ) : (
                            <>
                              <XCircle className="h-4 w-4 mr-2" />
                              Disable
                            </>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.totalPages} &mdash;{" "}
            {pagination.total} total organizations
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= pagination.totalPages}
              onClick={() =>
                setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {selectedOrg?.organizationName}
            </DialogTitle>
            <DialogDescription>
              Organization details and license information.
            </DialogDescription>
          </DialogHeader>
          {selectedOrg && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Type</p>
                  <Badge variant="secondary">{selectedOrg.organizationType}</Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Status</p>
                  {selectedOrg.disabled ? (
                    <Badge variant="destructive">Disabled</Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                      Active
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="text-sm font-medium">Allow Manual Charge</p>
                  <p className="text-xs text-muted-foreground">Enable manual payment approval for this org</p>
                </div>
                <Switch
                  checked={!!selectedOrg.org_manual_approval_enabled}
                  onCheckedChange={(val) => {
                    handleToggleManualApproval(selectedOrg, val);
                    setSelectedOrg({ ...selectedOrg, org_manual_approval_enabled: val });
                  }}
                />
              </div>
              <Separator />
              {(selectedOrg.firstName || selectedOrg.lastName) && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">
                    Contact Person
                  </p>
                  <p className="font-medium">
                    {selectedOrg.firstName} {selectedOrg.lastName}
                  </p>
                </div>
              )}
              {selectedOrg.email && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Email</p>
                  <p>{selectedOrg.email}</p>
                </div>
              )}
              {selectedOrg.phone && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Phone</p>
                  <p>{selectedOrg.phone}</p>
                </div>
              )}
              {(selectedOrg.city || selectedOrg.state) && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">
                    Location
                  </p>
                  <p>
                    {[selectedOrg.city, selectedOrg.state]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>
              )}
              <Separator />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">
                    Licenses Purchased
                  </p>
                  <p className="text-lg font-bold">
                    {selectedOrg.totalLicensesPurchased}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">
                    Licenses Used
                  </p>
                  <p className="text-lg font-bold">
                    {selectedOrg.totalLicensesUsed}
                  </p>
                </div>
              </div>
              {selectedOrg.planName && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Plan</p>
                  <p>{selectedOrg.planName}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">
                  Joined
                </p>
                <p>
                  {new Date(selectedOrg.createdAt).toLocaleDateString(
                    undefined,
                    { year: "numeric", month: "long", day: "numeric" }
                  )}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
